<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisField from 'dashboard/components-next/LaisField.vue';
import { participantReportLabel } from '../composables/salesCommissionParticipantLabels';

const props = defineProps({
  errors: { type: Object, default: () => ({}) },
  group: { type: String, required: true },
  reportTypes: { type: Array, default: () => [] },
  value: { type: Object, required: true },
});

const emit = defineEmits([
  'distributePercentages',
  'setCount',
  'toggleGroup',
  'updateGroup',
  'updateOptionistReport',
]);
const { t } = useI18n();
const isGeneralManager = computed(() => props.group === 'general_managers');
const isOptionist = computed(() => props.group === 'optionist_brokers');
const groupEnabled = computed(() => props.value.enabled !== false);
const countLimit = ref(Math.max(4, props.value.rows?.length || 0));
const selectedCount = ref(props.value.rows?.length || '');
const countOptions = computed(() =>
  Array.from({ length: countLimit.value }, (_, index) => index + 1)
);

watch(
  () => props.value.rows?.length,
  count => {
    if (count > countLimit.value) countLimit.value = Math.min(20, count);
    selectedCount.value = count || '';
  },
  { immediate: true }
);

const increaseCountOptions = () => {
  countLimit.value = Math.min(20, countLimit.value * 2);
  selectedCount.value = '';
};

const setCount = value => {
  selectedCount.value = value;
  emit('setCount', props.group, value);
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <label
      v-if="isGeneralManager"
      class="flex w-max items-center gap-2 text-sm font-semibold text-lais-text"
    >
      <input
        type="checkbox"
        :checked="groupEnabled"
        class="size-4 accent-lais-primary"
        @change="emit('toggleGroup', group, $event.target.checked)"
      />
      {{ t('SALES.COMMISSION.PARTICIPANTS.HAS_GROUP') }}
    </label>
    <template v-if="groupEnabled">
      <div v-if="isOptionist">
        <p class="mb-2 text-sm font-semibold text-lais-text">
          {{ t('SALES.COMMISSION.PARTICIPANTS.REPORT_TITLE') }}
        </p>
        <div class="flex flex-wrap gap-5">
          <label
            v-for="reportType in reportTypes"
            :key="reportType.id"
            class="flex items-center gap-2 text-sm text-lais-text"
          >
            <input
              type="radio"
              name="optionist-report-type"
              :value="reportType.id"
              :checked="String(value.report_type) === String(reportType.id)"
              class="accent-lais-primary"
              @change="emit('updateOptionistReport', reportType.id)"
            />
            {{ participantReportLabel(t, reportType.id) }}
          </label>
        </div>
      </div>
      <div class="grid gap-4 md:grid-cols-2">
        <LaisField
          :label="t('SALES.COMMISSION.PARTICIPANTS.TOTAL_COMMISSION')"
          :error="errors.total_percentage"
          required
        >
          <input
            :value="value.total_percentage"
            type="number"
            min="0"
            max="100"
            :disabled="isOptionist"
            class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm disabled:bg-lais-bg-soft"
            @input="
              emit(
                'updateGroup',
                group,
                'total_percentage',
                $event.target.value
              )
            "
          />
        </LaisField>
        <LaisField :label="t('SALES.COMMISSION.PARTICIPANTS.QUANTITY')">
          <div class="flex gap-2">
            <select
              :value="selectedCount"
              class="min-h-10 flex-1 rounded-lais border border-lais-border bg-white px-3 text-sm"
              @change="setCount($event.target.value)"
            >
              <option value="">
                {{ t('SALES.COMMISSION.COMMON.SELECT') }}
              </option>
              <option v-for="count in countOptions" :key="count" :value="count">
                {{ count }}
              </option>
            </select>
            <button
              v-if="countLimit < 20"
              type="button"
              class="rounded-lais border border-lais-border px-3 text-sm font-semibold text-lais-primary hover:bg-lais-primary-50"
              @click="increaseCountOptions"
            >
              {{ t('SALES.COMMISSION.PARTICIPANTS.MORE') }}
            </button>
          </div>
        </LaisField>
      </div>
      <button
        v-if="errors.form && value.rows?.length"
        type="button"
        class="self-start text-left text-sm font-semibold text-lais-primary hover:underline"
        @click="emit('distributePercentages', group)"
      >
        {{ t('SALES.COMMISSION.PARTICIPANTS.CALCULATE_PERCENTAGES') }}
      </button>
    </template>
  </div>
</template>
